import React, { useEffect, useState } from "react";
import api from "../utils/axios";
import {
  FiCheck,
  FiX,
  FiEye,
  FiCopy,
  FiSearch,
  FiFilter,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiAlertTriangle,
  FiRefreshCw,
  FiExternalLink,
  FiUser,
  FiDollarSign,
  FiCalendar,
} from "react-icons/fi";

const API_BASE = "https://api.aaveera365.in";

export default function ManualDepositRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusTab, setStatusTab] = useState("all"); // 'all' | 'pending' | 'approved' | 'rejected'
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Modals
  const [viewItem, setViewItem] = useState(null);
  const [approveItem, setApproveItem] = useState(null);
  const [rejectItem, setRejectItem] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [copiedUtr, setCopiedUtr] = useState(null);
  const [screenshotModal, setScreenshotModal] = useState(null);

  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "success" });
    }, 3500);
  };

  /* ================= FETCH REQUESTS ================= */
  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 10,
      };

      if (statusTab !== "all") {
        params.status = statusTab;
      }

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      try {
        const res = await api.get("/manual-deposit-requests", { params });
        if (res.data?.status || res.data?.success) {
          setRequests(res.data.data || []);
          setTotalPages(res.data.pagination?.totalPages || 1);
          setTotalRecords(res.data.pagination?.totalRecords || 0);
          return;
        }
      } catch (err) {
        // Fallback to live server endpoint /getManualDepositData
        const res = await api.get("/getManualDepositData", {
          params: {
            page,
            limit: 10,
            status: statusTab === "all" ? undefined : statusTab,
            search: searchTerm.trim() || undefined,
          },
        });
        const rows = res.data?.data || [];
        const normalized = rows.map((r) => ({
          id: r.transaction_id,
          user_id: r.uid,
          username: r.name || `User #${r.uid}`,
          amount: r.amount,
          utr_number: r.utr_no || r.utr_number || "—",
          payment_method_name: r.paymentmethod || "Manual Deposit",
          payment_method_type: r.payment_type || "bank",
          payment_screenshot: r.receipt || r.payment_screenshot,
          status: r.status === "accepted" ? "approved" : r.status,
          rejection_reason: r.rejection_reason,
          created_at: r.transaction_date,
          raw: r,
        }));
        setRequests(normalized);
        setTotalPages(res.data?.totalPages || 1);
        setTotalRecords(res.data?.totalRecords || rows.length);
      }
    } catch (err) {
      console.error("Error fetching manual deposit requests:", err);
      showToast(err.response?.data?.message || "Failed to load deposit requests", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [statusTab, page]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchRequests();
  };

  /* ================= COPY UTR ================= */
  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedUtr(text);
    setTimeout(() => setCopiedUtr(null), 2000);
  };

  /* ================= APPROVE ACTION ================= */
  const handleConfirmApprove = async () => {
    if (!approveItem) return;
    setActionLoading(true);

    try {
      try {
        const res = await api.post(`/manual-deposit-requests/${approveItem.id}/approve`);
        if (res.data?.status || res.data?.success) {
          showToast(res.data.message || "Deposit request approved successfully!");
          setApproveItem(null);
          fetchRequests();
          return;
        }
      } catch (err) {
        // Fallback to /approveRejectManualDeposit
        await api.post("/approveRejectManualDeposit", {
          trx: approveItem.raw || {
            transaction_id: approveItem.id,
            uid: approveItem.user_id,
            amount: approveItem.amount,
            utr_no: approveItem.utr_number,
          },
          isApproved: true,
        });
        showToast("Deposit request approved and wallet credited successfully!");
        setApproveItem(null);
        fetchRequests();
      }
    } catch (err) {
      console.error("Approve error:", err);
      showToast(err.response?.data?.message || "Failed to approve deposit request", "error");
    } finally {
      setActionLoading(false);
    }
  };

  /* ================= REJECT ACTION ================= */
  const handleConfirmReject = async () => {
    if (!rejectItem) return;
    if (!rejectionReason.trim()) {
      showToast("Please provide a rejection reason", "error");
      return;
    }

    setActionLoading(true);

    try {
      try {
        const res = await api.post(`/manual-deposit-requests/${rejectItem.id}/reject`, {
          rejection_reason: rejectionReason.trim(),
        });
        if (res.data?.status || res.data?.success) {
          showToast(res.data.message || "Deposit request rejected successfully!");
          setRejectItem(null);
          setRejectionReason("");
          fetchRequests();
          return;
        }
      } catch (err) {
        // Fallback to /approveRejectManualDeposit
        await api.post("/approveRejectManualDeposit", {
          trx: rejectItem.raw || {
            transaction_id: rejectItem.id,
            uid: rejectItem.user_id,
            amount: rejectItem.amount,
            utr_no: rejectItem.utr_number,
          },
          isApproved: false,
          rejection_reason: rejectionReason.trim(),
          reason: rejectionReason.trim(),
        });
        showToast("Deposit request rejected successfully!");
        setRejectItem(null);
        setRejectionReason("");
        fetchRequests();
      }
    } catch (err) {
      console.error("Reject error:", err);
      showToast(err.response?.data?.message || "Failed to reject deposit request", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const getFullImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    return `${API_BASE}${path}`;
  };

  const presetReasons = [
    "UTR number invalid / not found in bank statement",
    "Payment not received in admin bank account",
    "Screenshot blurry / incomplete / invalid proof",
    "Amount mismatch between screenshot and request",
    "Duplicate transaction submission",
  ];

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      {/* Toast Notification */}
      {toast.show && (
        <div
          className={`fixed top-5 right-5 z-50 px-4 py-3 rounded-xl shadow-lg border flex items-center gap-3 transition-all transform animate-bounce ${
            toast.type === "error"
              ? "bg-red-50 text-red-800 border-red-200"
              : "bg-emerald-50 text-emerald-800 border-emerald-200"
          }`}
        >
          {toast.type === "error" ? <FiXCircle className="w-5 h-5 text-red-500" /> : <FiCheckCircle className="w-5 h-5 text-emerald-500" />}
          <span className="text-sm font-medium">{toast.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Manual Deposit Requests</h1>
          <p className="text-sm text-gray-500 mt-1">
            Review and verify user deposit submissions, preview payment receipts, and approve wallet credits.
          </p>
        </div>

        <button
          onClick={fetchRequests}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-xl text-sm font-medium transition shadow-sm bg-white"
        >
          <FiRefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh Requests
        </button>
      </div>

      {/* ================= TABS & SEARCH BAR ================= */}
      <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Status Filter Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 md:pb-0">
          {[
            { id: "all", label: "All Requests" },
            { id: "pending", label: "Pending" },
            { id: "approved", label: "Approved" },
            { id: "rejected", label: "Rejected" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setStatusTab(tab.id);
                setPage(1);
              }}
              className={`px-4 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition ${
                statusTab === tab.id
                  ? "bg-gray-900 text-white shadow"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search Input */}
        <form onSubmit={handleSearch} className="flex items-center gap-2 w-full md:w-80">
          <div className="relative flex-1">
            <FiSearch className="absolute left-3.5 top-3 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by User ID, Name, UTR..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-gray-50 border border-gray-300 rounded-xl pl-9 pr-3.5 py-2 text-xs text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white transition"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-gray-900 hover:bg-black text-white text-xs font-semibold rounded-xl transition"
          >
            Search
          </button>
        </form>
      </div>

      {/* ================= REQUESTS TABLE ================= */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50/70 text-gray-600 text-xs uppercase tracking-wider font-semibold border-b border-gray-100">
              <tr>
                <th className="px-4 py-3.5">#</th>
                <th className="px-4 py-3.5">User Details</th>
                <th className="px-4 py-3.5">Payment Method</th>
                <th className="px-4 py-3.5">Deposit Amount</th>
                <th className="px-4 py-3.5">UTR / Tx ID</th>
                <th className="px-4 py-3.5">Screenshot</th>
                <th className="px-4 py-3.5">Submitted Date</th>
                <th className="px-4 py-3.5 text-center">Status</th>
                <th className="px-4 py-3.5 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={9} className="py-16 text-center text-gray-500">
                    <div className="inline-flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                      Loading deposit requests...
                    </div>
                  </td>
                </tr>
              ) : requests.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-16 text-center text-gray-400">
                    <div className="max-w-xs mx-auto">
                      <FiClock className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                      <p className="font-semibold text-gray-700">No deposit requests found</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {statusTab !== "all"
                          ? `There are currently no ${statusTab} deposit requests.`
                          : "User submitted manual deposit requests will appear here."}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                requests.map((r, index) => {
                  const status = (r.status || "pending").toLowerCase();
                  const screenshotUrl = getFullImageUrl(r.payment_screenshot);

                  return (
                    <tr key={r.id} className="hover:bg-gray-50/80 transition">
                      <td className="px-4 py-4 text-xs font-mono text-gray-500">
                        {(page - 1) * 10 + index + 1}
                      </td>

                      <td className="px-4 py-4">
                        <div className="font-semibold text-gray-900 text-sm">
                          {r.user_name || `User #${r.user_id}`}
                        </div>
                        <div className="text-xs font-mono text-gray-500 mt-0.5">
                          ID: {r.user_id}
                        </div>
                        {r.user_phone && (
                          <div className="text-xs text-gray-400">{r.user_phone}</div>
                        )}
                      </td>

                      <td className="px-4 py-4 text-xs">
                        <div className="font-semibold text-gray-800">
                          {r.payment_method_name || "Manual Payment"}
                        </div>
                        <div className="mt-0.5">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                              r.payment_method_type === "bank"
                                ? "bg-blue-50 text-blue-700"
                                : r.payment_method_type === "upi"
                                ? "bg-purple-50 text-purple-700"
                                : "bg-amber-50 text-amber-700"
                            }`}
                          >
                            {r.payment_method_type === "bank"
                              ? "Bank Transfer"
                              : r.payment_method_type === "upi"
                              ? "UPI"
                              : "QR Code"}
                          </span>
                        </div>
                      </td>

                      <td className="px-4 py-4 font-bold text-gray-900 text-sm">
                        ₹{Number(r.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </td>

                      <td className="px-4 py-4 text-xs">
                        <div className="flex items-center gap-1.5 font-mono font-medium text-gray-900 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1 w-fit">
                          <span>{r.utr_number}</span>
                          <button
                            onClick={() => copyToClipboard(r.utr_number)}
                            className="p-1 hover:text-emerald-600 transition"
                            title="Copy UTR"
                          >
                            {copiedUtr === r.utr_number ? (
                              <FiCheck className="w-3.5 h-3.5 text-emerald-600" />
                            ) : (
                              <FiCopy className="w-3.5 h-3.5 text-gray-400" />
                            )}
                          </button>
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        {screenshotUrl ? (
                          <div
                            onClick={() => setScreenshotModal(screenshotUrl)}
                            className="relative group cursor-pointer inline-block"
                            title="Click to view payment proof"
                          >
                            <img
                              src={screenshotUrl}
                              alt="Proof"
                              className="h-10 w-10 object-cover rounded-lg border border-gray-200 bg-white p-0.5 shadow-sm group-hover:opacity-75 transition"
                            />
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition bg-black/40 rounded-lg">
                              <FiEye className="w-4 h-4 text-white" />
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>

                      <td className="px-4 py-4 text-xs text-gray-500">
                        {r.created_at ? new Date(r.created_at).toLocaleString("en-IN") : "—"}
                      </td>

                      <td className="px-4 py-4 text-center">
                        <span
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                            status === "approved" || status === "accepted"
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                              : status === "rejected"
                              ? "bg-red-50 text-red-700 border border-red-200"
                              : "bg-amber-50 text-amber-700 border border-amber-200"
                          }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              status === "approved" || status === "accepted"
                                ? "bg-emerald-500"
                                : status === "rejected"
                                ? "bg-red-500"
                                : "bg-amber-500"
                            }`}
                          />
                          {status === "approved" || status === "accepted"
                            ? "Approved"
                            : status === "rejected"
                            ? "Rejected"
                            : "Pending"}
                        </span>
                      </td>

                      <td className="px-4 py-4 text-right">
                        <div className="inline-flex items-center gap-1.5">
                          {/* View Details */}
                          <button
                            onClick={() => setViewItem(r)}
                            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
                            title="View Transaction Details"
                          >
                            <FiEye className="w-4 h-4" />
                          </button>

                          {status === "pending" && (
                            <>
                              {/* Approve Button */}
                              <button
                                onClick={() => setApproveItem(r)}
                                className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold transition flex items-center gap-1 shadow-sm"
                                title="Approve & Credit Wallet"
                              >
                                <FiCheck className="w-3.5 h-3.5" />
                                Approve
                              </button>

                              {/* Reject Button */}
                              <button
                                onClick={() => {
                                  setRejectItem(r);
                                  setRejectionReason("");
                                }}
                                className="px-2.5 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-semibold transition flex items-center gap-1 shadow-sm"
                                title="Reject Deposit"
                              >
                                <FiX className="w-3.5 h-3.5" />
                                Reject
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100 bg-gray-50/50">
            <span className="text-xs text-gray-500">
              Showing page <span className="font-semibold">{page}</span> of{" "}
              <span className="font-semibold">{totalPages}</span> ({totalRecords} records)
            </span>

            <div className="flex items-center gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                Previous
              </button>

              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ================= VIEW DETAILS MODAL ================= */}
      {viewItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
          onClick={() => setViewItem(null)}
        >
          <div
            className="relative bg-white rounded-2xl p-6 max-w-2xl w-full shadow-2xl my-8 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-4 mb-4 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Deposit Request Details</h3>
                <p className="text-xs text-gray-500">Request #{viewItem.id}</p>
              </div>
              <button
                onClick={() => setViewItem(null)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column: Details */}
              <div className="space-y-4 text-xs">
                <div className="bg-gray-50 rounded-xl p-3.5 space-y-2 border border-gray-200">
                  <div className="font-bold text-gray-700 uppercase tracking-wider text-[11px]">User Information</div>
                  <div><span className="text-gray-500">Name:</span> <span className="font-semibold text-gray-900">{viewItem.user_name || "N/A"}</span></div>
                  <div><span className="text-gray-500">User ID:</span> <span className="font-mono text-gray-900 font-semibold">{viewItem.user_id}</span></div>
                  {viewItem.user_phone && <div><span className="text-gray-500">Phone:</span> <span className="text-gray-900">{viewItem.user_phone}</span></div>}
                  {viewItem.user_email && <div><span className="text-gray-500">Email:</span> <span className="text-gray-900">{viewItem.user_email}</span></div>}
                </div>

                <div className="bg-gray-50 rounded-xl p-3.5 space-y-2 border border-gray-200">
                  <div className="font-bold text-gray-700 uppercase tracking-wider text-[11px]">Transaction Information</div>
                  <div>
                    <span className="text-gray-500">Deposit Amount:</span>{" "}
                    <span className="font-bold text-base text-emerald-700">₹{Number(viewItem.amount).toLocaleString("en-IN")}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">UTR / Tx ID:</span>{" "}
                    <span className="font-mono font-semibold text-gray-900">{viewItem.utr_number}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Payment Method:</span>{" "}
                    <span className="font-semibold text-gray-900">{viewItem.payment_method_name || "Manual"} ({viewItem.payment_method_type})</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Submitted At:</span>{" "}
                    <span className="text-gray-900">{new Date(viewItem.created_at).toLocaleString("en-IN")}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Current Status:</span>{" "}
                    <span className="capitalize font-bold text-gray-900">{viewItem.status}</span>
                  </div>
                  {viewItem.approved_at && (
                    <div>
                      <span className="text-gray-500">Processed At:</span>{" "}
                      <span className="text-gray-900">{new Date(viewItem.approved_at).toLocaleString("en-IN")}</span>
                    </div>
                  )}
                  {viewItem.rejection_reason && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg text-red-800">
                      <span className="font-semibold">Rejection Reason:</span> {viewItem.rejection_reason}
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column: Screenshot */}
              <div>
                <div className="font-bold text-gray-700 uppercase tracking-wider text-[11px] mb-2">Payment Screenshot Proof</div>
                {viewItem.payment_screenshot ? (
                  <div className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50 p-2 text-center">
                    <img
                      src={getFullImageUrl(viewItem.payment_screenshot)}
                      alt="Payment Receipt Proof"
                      className="max-h-64 mx-auto object-contain rounded-lg"
                    />
                    <a
                      href={getFullImageUrl(viewItem.payment_screenshot)}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-3 inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-semibold"
                    >
                      <FiExternalLink className="w-3.5 h-3.5" />
                      Open Full Size Image
                    </a>
                  </div>
                ) : (
                  <div className="h-40 border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center text-gray-400 text-xs">
                    No screenshot uploaded
                  </div>
                )}
              </div>
            </div>

            {/* Modal Actions */}
            <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setViewItem(null)}
                className="px-4 py-2 border border-gray-300 rounded-xl text-xs font-semibold text-gray-700 hover:bg-gray-50 transition"
              >
                Close
              </button>

              {viewItem.status?.toLowerCase() === "pending" && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setApproveItem(viewItem);
                      setViewItem(null);
                    }}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold transition flex items-center gap-1.5 shadow"
                  >
                    <FiCheck className="w-4 h-4" />
                    Approve & Credit Wallet
                  </button>

                  <button
                    onClick={() => {
                      setRejectItem(viewItem);
                      setRejectionReason("");
                      setViewItem(null);
                    }}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-semibold transition flex items-center gap-1.5 shadow"
                  >
                    <FiX className="w-4 h-4" />
                    Reject Deposit
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ================= APPROVE CONFIRMATION MODAL ================= */}
      {approveItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => !actionLoading && setApproveItem(null)}
        >
          <div
            className="relative bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4 text-emerald-600">
              <FiCheckCircle className="w-7 h-7" />
            </div>

            <h3 className="text-lg font-bold text-gray-900 text-center">Approve Manual Deposit</h3>
            <p className="text-xs text-gray-500 text-center mt-1">
              Are you sure you want to verify and approve this deposit request?
            </p>

            <div className="my-5 p-4 bg-emerald-50/60 border border-emerald-200/80 rounded-xl space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-500">User:</span>
                <span className="font-semibold text-gray-900">{approveItem.user_name || `User #${approveItem.user_id}`}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Deposit Amount:</span>
                <span className="font-bold text-sm text-emerald-700">₹{Number(approveItem.amount).toLocaleString("en-IN")}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">UTR Number:</span>
                <span className="font-mono font-semibold text-gray-900">{approveItem.utr_number}</span>
              </div>
              <div className="pt-2 border-t border-emerald-200 text-emerald-800 text-[11px] leading-relaxed">
                ✓ Exactly <strong>₹{Number(approveItem.amount).toLocaleString("en-IN")}</strong> will be automatically credited to the user's wallet.
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                disabled={actionLoading}
                onClick={() => setApproveItem(null)}
                className="flex-1 py-2.5 border border-gray-300 rounded-xl text-xs font-semibold text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={actionLoading}
                onClick={handleConfirmApprove}
                className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold transition shadow flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                {actionLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Crediting...
                  </>
                ) : (
                  <>
                    <FiCheck className="w-4 h-4" />
                    Confirm & Credit Wallet
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= REJECT MODAL ================= */}
      {rejectItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => !actionLoading && setRejectItem(null)}
        >
          <div
            className="relative bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4 text-red-600">
              <FiAlertTriangle className="w-7 h-7" />
            </div>

            <h3 className="text-lg font-bold text-gray-900 text-center">Reject Deposit Request</h3>
            <p className="text-xs text-gray-500 text-center mt-1">
              Please specify the reason for rejecting request #{rejectItem.id}. The user will be notified with this reason.
            </p>

            <div className="my-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Preset Rejection Reasons
                </label>
                <select
                  className="w-full bg-white border border-gray-300 rounded-xl px-3 py-2 text-xs text-gray-900 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  onChange={(e) => setRejectionReason(e.target.value)}
                  defaultValue=""
                >
                  <option value="" disabled>-- Select a standard reason --</option>
                  {presetReasons.map((reason, idx) => (
                    <option key={idx} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Rejection Reason (Required) <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={3}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-xs text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                  placeholder="Enter specific details explaining why this request cannot be approved..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                disabled={actionLoading}
                onClick={() => setRejectItem(null)}
                className="flex-1 py-2.5 border border-gray-300 rounded-xl text-xs font-semibold text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={actionLoading || !rejectionReason.trim()}
                onClick={handleConfirmReject}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-semibold transition shadow flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Rejecting...
                  </>
                ) : (
                  <>
                    <FiX className="w-4 h-4" />
                    Confirm Rejection
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= SCREENSHOT FULL PREVIEW MODAL ================= */}
      {screenshotModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setScreenshotModal(null)}
        >
          <div
            className="relative bg-white rounded-2xl p-4 max-w-xl w-full shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-900">Payment Screenshot Proof</h3>
              <button
                onClick={() => setScreenshotModal(null)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-lg transition"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            <div className="flex justify-center bg-gray-50 rounded-xl p-3">
              <img
                src={screenshotModal}
                alt="Payment Screenshot"
                className="max-h-[75vh] max-w-full object-contain rounded-lg"
              />
            </div>

            <div className="mt-3 flex justify-end">
              <a
                href={screenshotModal}
                target="_blank"
                rel="noreferrer"
                className="px-4 py-2 bg-gray-900 text-white text-xs font-semibold rounded-lg hover:bg-black transition flex items-center gap-1.5"
              >
                <FiExternalLink className="w-3.5 h-3.5" />
                Open In New Tab
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
